import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient';
import { toast } from 'sonner';
import { Database } from '@/_constants/database.types';
import { PaginationState } from '@tanstack/react-table';

type PastPaperRow = Database['public']['Tables']['past_papers']['Row'];

export type AdminPastPaper = Pick<PastPaperRow, 'id' | 'title' | 'subject' | 'exam_board' | 'paper_number' | 'paper_code' | 'date' | 'status'>;

export interface PastPaperFilters {
  subject?: string;
  examBoard?: string;
  paperNumber?: string;
  year?: string;
}

export interface UseAdminPastPapersParams {
  pagination: PaginationState;
  filters?: PastPaperFilters;
}

export interface AdminPastPapersResult {
  data: AdminPastPaper[];
  rowCount: number;
}

const fetchAdminPastPapers = async (
  params: UseAdminPastPapersParams
): Promise<AdminPastPapersResult> => {
  let query = supabaseClient
    .from('past_papers')
    .select('id, title, subject, exam_board, paper_number, paper_code, date, status', { count: 'exact' });

  // Apply filters
  const { filters } = params;
  if (filters) {
    if (filters.subject) {
      query = query.eq('subject', filters.subject);
    }
    if (filters.examBoard) {
      query = query.eq('exam_board', filters.examBoard);
    }
    if (filters.paperNumber) {
      query = query.eq('paper_number', filters.paperNumber);
    }
    if (filters.year) {
      // Filter by year - date is stored as YYYY-MM-DD
      const startOfYear = `${filters.year}-01-01`;
      const endOfYear = `${filters.year}-12-31`;
      query = query.gte('date', startOfYear).lte('date', endOfYear);
    }
  }

  // Default sort by date descending
  query = query.order('date', { ascending: false });

  // Apply pagination
  const { pageIndex, pageSize } = params.pagination;
  const from = pageIndex * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching past papers:', error);
    toast.error('Failed to load past papers. Please try again.');
    throw error;
  }

  return {
    data: data || [],
    rowCount: count ?? 0,
  };
};

const useAdminPastPapers = (params: UseAdminPastPapersParams) => {
  return useQuery({
    queryKey: ['admin-past-papers', params],
    queryFn: () => fetchAdminPastPapers(params),
    placeholderData: keepPreviousData,
  });
};

const deletePastPaper = async (id: string): Promise<void> => {
  // First, fetch the past paper to get the pdf_url
  const { data: pastPaper, error: fetchError } = await supabaseClient
    .from('past_papers')
    .select('pdf_url')
    .eq('id', id)
    .single();

  if (fetchError) {
    console.error('Error fetching past paper:', fetchError);
    throw fetchError;
  }

  // Delete from storage if pdf_url exists
  if (pastPaper?.pdf_url) {
    // Extract the file path from the URL or use it directly if it's already a path
    const filePath = pastPaper.pdf_url.includes('past-papers/')
      ? pastPaper.pdf_url.split('past-papers/')[1]
      : pastPaper.pdf_url;

    // Delete the main PDF
    const { error: storageError } = await supabaseClient.storage
      .from('past-papers')
      .remove([filePath]);

    if (storageError) {
      console.warn('Failed to delete PDF from storage:', storageError.message);
      // Continue with database deletion even if storage deletion fails
    }

    // Try to delete mark scheme as well (may not exist)
    const markSchemeFilePath = filePath.replace('.pdf', '-mark-scheme.pdf');
    await supabaseClient.storage
      .from('past-papers')
      .remove([markSchemeFilePath]);
  }

  // Delete from database
  const { error } = await supabaseClient.from('past_papers').delete().eq('id', id);

  if (error) {
    console.error('Error deleting past paper:', error);
    throw error;
  }
};

export const useDeletePastPaper = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePastPaper,
    onSuccess: () => {
      toast.success('Past paper deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-past-papers'] });
    },
    onError: () => {
      toast.error('Failed to delete past paper. Please try again.');
    },
  });
};

// Update a past paper
interface UpdatePastPaperParams {
  pastPaperId: string;
  updates: {
    title?: string;
    status?: 'draft' | 'published' | 'archived';
    reading_insert_page_numbers?: number[] | null;
    reading_insert_page_numbers_2?: number[] | null;
  };
}

const updatePastPaper = async ({ pastPaperId, updates }: UpdatePastPaperParams): Promise<void> => {
  const { error } = await supabaseClient
    .from('past_papers')
    .update(updates)
    .eq('id', pastPaperId);

  if (error) {
    console.error('Error updating past paper:', error);
    throw error;
  }
};

export const useUpdatePastPaper = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePastPaper,
    onSuccess: (_, { pastPaperId }) => {
      toast.success('Past paper updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-past-papers'] });
      queryClient.invalidateQueries({ queryKey: ['admin-past-paper', pastPaperId] });
    },
    onError: () => {
      toast.error('Failed to update past paper. Please try again.');
    },
  });
};

export default useAdminPastPapers;
